/**
 * Preferences Step
 *
 * Third step of the intake wizard - collects communication preferences.
 */

import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SmsIcon from '@mui/icons-material/Sms';
import {
  Box,
  FormControlLabel,
  GlassCard,
  GlassCheckbox,
  Grid,
  MagicSelect,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { IntakeFormData } from '../IntakeWizard';

interface PreferencesStepProps {
  formData: IntakeFormData;
  updateFormData: (updates: Partial<IntakeFormData>) => void;
}

const CONTACT_METHODS = [
  { value: 'email', label: 'Email', icon: <EmailIcon />, description: 'Receive updates via email' },
  { value: 'phone', label: 'Phone Call', icon: <PhoneIcon />, description: 'Prefer phone conversations' },
  { value: 'sms', label: 'Text Message', icon: <SmsIcon />, description: 'Quick updates via SMS' },
];

const CONTACT_TIMES = [
  { value: 'morning', label: 'Morning (8am - 12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm - 5pm)' },
  { value: 'evening', label: 'Evening (5pm - 8pm)' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

export default function PreferencesStep({ formData, updateFormData }: PreferencesStepProps) {
  const { t } = useI18n();
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Communication Preferences
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        Let us know how you'd like us to keep in touch.
      </Typography>

      {/* Preferred Contact Method */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          How would you prefer to be contacted?
        </Typography>
        <Grid container spacing={2}>
          {CONTACT_METHODS.map((method) => (
            <Grid size={{ xs: 12, sm: 4 }} key={method.value}>
              <GlassCard
                onClick={() => updateFormData({ preferredContact: method.value as IntakeFormData['preferredContact'] })}
                sx={{
                  cursor: 'pointer',
                  border: formData.preferredContact === method.value ? '2px solid' : '1px solid transparent',
                  borderColor: formData.preferredContact === method.value ? 'primary.main' : 'transparent',
                  textAlign: 'center',
                  py: 3,
                }}
              >
                <Box
                  sx={{ mb: 1, color: formData.preferredContact === method.value ? 'primary.main' : 'text.secondary' }}
                >
                  {method.icon}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {method.label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {method.description}
                </Typography>
              </GlassCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Time Preferences */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicSelect
            fullWidth
            label={t('onboarding.timezone')}
            value={formData.timezone}
            onChange={(e) => updateFormData({ timezone: e.target.value as string })}
            options={TIMEZONES}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicSelect
            fullWidth
            label={t('onboarding.bestTimeToContact')}
            value={formData.bestTimeToContact}
            onChange={(e) =>
              updateFormData({ bestTimeToContact: e.target.value as IntakeFormData['bestTimeToContact'] })
            }
            options={[{ value: '', label: t('onboarding.noPreference') }, ...CONTACT_TIMES]}
          />
        </Grid>
      </Grid>

      {/* Marketing Opt-in */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControlLabel
          control={
            <GlassCheckbox
              checked={formData.marketingOptIn}
              onChange={(e) => updateFormData({ marketingOptIn: e.target.checked })}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Keep me informed about market updates and opportunities
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Receive occasional emails about market trends, new loan programs, and investment opportunities.
              </Typography>
            </Box>
          }
        />
      </Box>
    </Box>
  );
}
